'use client';

import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Currency } from 'hotdesk_typescript';
import { RootState } from '../store';
import storage from 'redux-persist/lib/storage'; // Import directly from redux-persist
import { CurrencyService } from '@/hotdesk-ui/services/currency.service';
import { fetchClientIpCountry } from '@/hotdesk-ui/helpers/client-ip-country';
import { persistReducer } from 'redux-persist';

interface InitialStateCurrency {
    currency: Currency[];
    defaultCurrency: Currency;
    /**
     * True once the visitor picks a currency themselves. Only then is
     * `defaultCurrency` final; otherwise it is re-detected on every visit,
     * so a wrong first guess (a failed Firebase fetch, a VPN, a trip abroad)
     * doesn't stick for good.
     */
    currencyUserSelected: boolean;
}

const FALLBACK_CURRENCY: Currency = { code: 'USD', name: 'USD' };

const initialState: InitialStateCurrency = {
    currency: [],
    defaultCurrency: { code: '', name: '' },
    currencyUserSelected: false
};

export const getDefaultCurrency = createAsyncThunk(
    'currency/getDefaultCurrency',
    async ({
        currencies
    }: { currencies: Currency[]}, { getState }) => {
        const state = getState() as RootState;
        const { defaultLanguage } = state.listing;
        try {
            const apiConfig = await (new ApiConfigration()).getApiConfig(
                defaultLanguage
            );
            const service = new CurrencyService(apiConfig);
            // IpGeoBootstrap usually has the edge-resolved country by now;
            // look it up directly when it hasn't landed yet.
            const getIpCountry = async () =>
                state.geoLocation.sessionIpCountry ?? fetchClientIpCountry();
            const defaultCurrency = await service.getDefaultCurrency(
                currencies,
                getIpCountry
            );
            return JSON.parse(JSON.stringify(defaultCurrency));
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);

export const getCurrency = createAsyncThunk(
    'currency/getCurrency',
    async (_, { getState }) => {
        const state = getState() as RootState;
        const { defaultLanguage } = state.listing;
        try {
            const apiConfig = await (new ApiConfigration()).getApiConfig(
                defaultLanguage
            );
            const service = new CurrencyService(apiConfig);
            const currencyRes = await service.getCurrencies(defaultLanguage);
            return JSON.parse(JSON.stringify(currencyRes));
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);

export const currencySlice = createSlice({
    name: 'currency',
    initialState,
    reducers: {
        /** The visitor picked a currency: keep it and stop auto-detecting. */
        setDefaultCurrency: (state, action) => {
            state.defaultCurrency = action.payload?.code
                ? action.payload
                : FALLBACK_CURRENCY;
            state.currencyUserSelected = true;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCurrency.fulfilled, (state, action) => {
                state.currency = action.payload.currencyList as Currency[];
            })
            // Failures only fill an empty slot. They never replace a currency
            // the visitor already has, and detection retries next visit.
            .addCase(getCurrency.rejected, (state) => {
                if (!state.defaultCurrency.code) {
                    state.defaultCurrency = FALLBACK_CURRENCY;
                }
            })
            .addCase(getDefaultCurrency.fulfilled, (state, action) => {
                // The visitor may have picked one while detection was running.
                if (state.currencyUserSelected) return;
                const currency = action.payload as Currency;
                if (currency?.code) {
                    state.defaultCurrency = currency;
                } else if (!state.defaultCurrency.code) {
                    state.defaultCurrency = FALLBACK_CURRENCY;
                }
            })
            .addCase(getDefaultCurrency.rejected, (state) => {
                if (!state.defaultCurrency.code) {
                    state.defaultCurrency = FALLBACK_CURRENCY;
                }
            });
    }
});

export const { setDefaultCurrency } = currencySlice.actions;

const persistConfig = {
    key: 'currency',
    whitelist: ['defaultCurrency', 'currencyUserSelected'],
    storage: storage
}

export default persistReducer(persistConfig, currencySlice.reducer);
